import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LoadingState } from '@/components/ui/loading-state';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/contexts/unified-permissions';
import { toast } from 'sonner';
import { useChatMessages, useChatOperations } from '../hooks';
import { ChatBubble } from './ChatBubble';
import { ChatInput } from './ChatInput';
import { ADMIN_USER_ID } from '../constants';

/**
 * Chat interface component without dialog wrapper
 * Used for embedding chat functionality within other components
 */
interface ChatInterfaceProps {
  otherUserId?: string;
  otherUserName?: string;
  className?: string;
  height?: string;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  otherUserId = ADMIN_USER_ID,
  otherUserName = 'Support',
  className,
  height = 'h-[400px]',
}) => {
  const { user } = usePermissions();
  const [newMessage, setNewMessage] = useState('');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Chat hooks
  const {
    messages,
    loading: messagesLoading,
    error: messagesError,
    addOptimisticMessage,
    removeOptimisticMessage
  } = useChatMessages(otherUserId);
  const { sendMessage, markMessagesAsRead, loading: operationsLoading } = useChatOperations();

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback((immediate = false) => {
    if (messagesEndRef.current) {
      // Use immediate scroll for optimistic messages on mobile
      const behavior = immediate ? 'auto' : 'smooth';
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0) {
      // Use requestAnimationFrame for better mobile performance
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [messages, scrollToBottom]);

  // Mark messages as read when component mounts or messages change
  useEffect(() => {
    if (user?.id && otherUserId && messages.length > 0) {
      const unreadMessages = messages.filter(
        msg => msg.senderUserId === otherUserId && !msg.readAt
      );
      
      if (unreadMessages.length > 0) {
        markMessagesAsRead(otherUserId);
      }
    }
  }, [user?.id, otherUserId, messages, markMessagesAsRead]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user?.id || !otherUserId) {
      return;
    }

    const messageContent = newMessage.trim();
    setNewMessage(''); // Clear input immediately for better UX

    // Add optimistic message immediately
    const tempId = addOptimisticMessage(messageContent, user.id, otherUserId);

    // Force immediate scroll for optimistic message on mobile
    // Use setTimeout to ensure the DOM has updated with the new message
    setTimeout(() => {
      scrollToBottom(true); // immediate scroll for better mobile UX
    }, 0);

    try {
      const success = await sendMessage(otherUserId, messageContent);
      if (!success) {
        // Remove optimistic message and restore input if send failed
        removeOptimisticMessage(tempId);
        setNewMessage(messageContent);
        toast.error('Failed to send message. Please try again.');
      }
      // If successful, the real message will come via realtime subscription
      // and replace the optimistic one
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove optimistic message and restore input
      removeOptimisticMessage(tempId);
      setNewMessage(messageContent);
      toast.error('Failed to send message. Please try again.');
    }
  };

  if (!user) {
    return (
      <div className={cn('flex items-center justify-center', height, className)}>
        <p className="text-sm text-muted-foreground">Please log in to use chat.</p>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col border rounded-lg bg-background', height, className)}>
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b bg-muted/50 flex-shrink-0">
        <MessageCircle className="h-4 w-4" />
        <span className="font-medium">Chat with {otherUserName}</span>
      </div>

      {/* Messages area */}
      <div className="flex-1 flex flex-col min-h-0">
        {messagesError && (
          <Alert variant="destructive" className="m-4">
            <AlertDescription>
              Failed to load messages. Please try refreshing the chat.
            </AlertDescription>
          </Alert>
        )}

        {messagesLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <LoadingState description="Loading messages..." />
          </div>
        ) : (
          <ScrollArea 
            ref={scrollAreaRef}
            className="flex-1 px-4"
          >
            <div className="py-4">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-center">
                  <MessageCircle className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No messages yet. Start the conversation!
                  </p>
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <ChatBubble
                      key={message.id}
                      message={message}
                      isOwnMessage={message.senderUserId === user.id}
                    />
                  ))}
                  {/* Scroll anchor */}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 p-4 border-t bg-muted/30">
        <ChatInput
          value={newMessage}
          onChange={setNewMessage}
          onSend={handleSendMessage}
          disabled={operationsLoading || messagesLoading}
          placeholder={`Message ${otherUserName}...`}
        />
      </div>
    </div>
  );
};
